import React from 'react';
import {
  RadioGroup,
  Radio,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';

// How a phase-date edit propagates along the swap chain. The two modes:
//   Cascade — only the adjacent phase absorbs the change; later phases stay put.
//   Ripple  — every later phase shifts by the same amount, keeping durations.
// Backed by a single boolean preference (ripple = true) so it stays compatible
// with the persisted usePhaseCascadePref store and lib/phaseDates CascadeMode.

const CASCADE_INFO =
  'Moving a phase date adjusts only the adjacent phase; later phases stay put.';
const RIPPLE_INFO =
  'Moving a phase date shifts every later phase by the same amount, keeping their durations.';

const useStyles = makeStyles({
  labelRow: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
  },
  info: {
    display: 'inline-flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
    cursor: 'help',
  },
});

interface PhaseCascadeChoiceProps {
  /** true = Ripple, false = Cascade. */
  ripple: boolean;
  onChange: (ripple: boolean) => void;
  /** RadioGroup layout. Defaults to horizontal. */
  layout?: 'horizontal' | 'horizontal-stacked' | 'vertical';
}

const PhaseCascadeChoice: React.FC<PhaseCascadeChoiceProps> = ({
  ripple,
  onChange,
  layout = 'horizontal',
}) => {
  const styles = useStyles();
  const labelWithInfo = (text: string, info: string) => (
    <span className={styles.labelRow}>
      {text}
      <Tooltip content={info} relationship="description">
        {/* Stop the click from also toggling the radio — the icon is purely
            informational (hover/focus shows the tooltip). */}
        <span
          className={styles.info}
          tabIndex={0}
          role="img"
          aria-label={`${text}: ${info}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Info16Regular />
        </span>
      </Tooltip>
    </span>
  );

  return (
    <RadioGroup
      layout={layout}
      value={ripple ? 'ripple' : 'cascade'}
      onChange={(_e, data) => onChange(data.value === 'ripple')}
    >
      <Radio value="cascade" label={labelWithInfo('Cascade', CASCADE_INFO)} />
      <Radio value="ripple" label={labelWithInfo('Ripple', RIPPLE_INFO)} />
    </RadioGroup>
  );
};

export default PhaseCascadeChoice;
